import { useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { bulkRetry, domainsPage, getDomain } from "@/api/domains";
import type { DomainSummary, RunDetail } from "@/api/types";

const PAGE_SIZE = 25;

/**
 * Errored-domain drill-in at the bottom of `/runs/:id`.
 *
 * Lists domains where the worker hit an internal failure during
 * extraction (GPT crash, parser bug, worker exception). Scope is
 * `status: error` only — `unreachable` (the dominant non-success on the
 * web) is left to the OutcomeBar count since it's expected noise rather
 * than something to investigate per-row.
 *
 * The last event message is fetched per visible row from
 * `/api/domains/{id}` since that endpoint already returns the events
 * timeline and we don't want to extend the list response for one
 * surface. Capped at PAGE_SIZE rows per page so the fan-out stays
 * bounded.
 */
export function RunErrors({ run }: { run: RunDetail }) {
  const qc = useQueryClient();
  const errorCount = run.progress.by_status.error ?? 0;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const query = useInfiniteQuery({
    queryKey: ["run-errors", run.id],
    queryFn: ({ pageParam }) =>
      domainsPage(pageParam ?? null, {
        run_id: run.id,
        status: "error",
        limit: PAGE_SIZE,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: errorCount > 0,
  });

  const retryAll = useMutation({
    mutationFn: () =>
      bulkRetry({ filter: { run_id: run.id, status: "error" } }),
    onSuccess: (r) => {
      if (r.async) {
        toast.success(
          `Bulk retry queued · ${r.affected.toLocaleString()} domains will run via worker`,
        );
      } else {
        toast.success(`Retry applied to ${r.affected.toLocaleString()} domain(s)`);
      }
      if (r.capped) toast.warning("Match was capped — re-run to continue.");
      // Refresh the error list (rows will drop as workers process them) and
      // the run's progress counts so the Errors card header updates.
      qc.invalidateQueries({ queryKey: ["run-errors", run.id] });
      qc.invalidateQueries({ queryKey: ["run", run.id] });
      qc.invalidateQueries({ queryKey: ["domains"] });
      setConfirmOpen(false);
    },
  });

  if (errorCount === 0) return null;

  const rows: DomainSummary[] = (query.data?.pages ?? []).flatMap((p) => p.items);

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between mb-3 gap-3">
        <h2 className="text-sm font-semibold">
          Errors{" "}
          <span className="text-muted font-normal">
            ({errorCount.toLocaleString()})
          </span>
        </h2>
        <div className="flex items-center gap-3">
          <button
            className="btn text-xs"
            onClick={() => setConfirmOpen(true)}
            disabled={retryAll.isPending}
            title="Reset every error in this run to PENDING and re-enqueue"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Retry all errors
          </button>
          <Link
            to="/domains"
            search={{ run_id: run.id, status: "error" }}
            className="text-xs text-muted hover:text-accent hover:underline"
          >
            View all in domains →
          </Link>
        </div>
      </div>

      {query.isLoading ? (
        <div className="text-sm text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted">
          No errored domains found for this run.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((d) => (
            <ErrorRow key={d.id} domain={d} />
          ))}
        </ul>
      )}

      {query.hasNextPage ? (
        <div className="mt-3">
          <button
            className="btn-ghost text-xs"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? "Loading…" : "Show more"}
          </button>
        </div>
      ) : null}

      {confirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="card w-full max-w-md p-5 space-y-3">
            <h3 className="text-base font-semibold">Retry all errors?</h3>
            <p className="text-sm text-muted">
              About to reset {errorCount.toLocaleString()} errored domain(s)
              for this run to PENDING and re-enqueue them.
              {errorCount > 10_000 ? (
                <>
                  {" "}
                  Above 10,000 rows the dispatch fans out via a worker
                  actor, so the API returns immediately and the queue
                  fills in the background.
                </>
              ) : null}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="btn-ghost"
                onClick={() => setConfirmOpen(false)}
                disabled={retryAll.isPending}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={() => retryAll.mutate()}
                disabled={retryAll.isPending}
              >
                {retryAll.isPending ? "Working…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ErrorRow({ domain }: { domain: DomainSummary }) {
  const detail = useQueries({
    queries: [
      {
        queryKey: ["domain", domain.id],
        queryFn: () => getDomain(domain.id),
        staleTime: 30_000,
      },
    ],
  })[0];

  // Last event is the failure — events come back sorted ascending by
  // `at` from the server, so the tail is the most recent. Fall back to
  // `init_error` for producer-side failures that have no events row.
  const lastEvent = detail.data?.events?.at(-1);
  const message = lastEvent?.event ?? domain.init_error ?? null;

  return (
    <li className="py-2.5 flex items-baseline gap-3 text-sm">
      <Link
        to="/domains/$domainId"
        params={{ domainId: domain.id }}
        className="font-mono text-xs hover:text-accent hover:underline truncate max-w-[28rem] shrink-0"
        title={domain.normalized_url}
      >
        {domain.normalized_url}
      </Link>
      <span
        className="text-xs text-muted truncate flex-1 min-w-0"
        title={message ?? undefined}
      >
        {detail.isLoading
          ? "…"
          : (message ?? <span className="italic">no error message logged</span>)}
      </span>
    </li>
  );
}
